import { Input, Modal, Select, Form } from "antd";
import PropTypes from "prop-types";
import { useState } from "react";

import { getBackendErrorDetail } from "../../helpers/GetStaticData.js";
import { useAlertStore } from "../../store/alert-store.js";
import { appDeploymentsService } from "./app-deployments-service.js";
import "./AppDeployment.css";
import { useExceptionHandler } from "../../hooks/useExceptionHandler";

const defaultFromDetails = {
  app_name: "",
  description: "",
  workflow: "",
  app_display_name: "",
};

const AppDeployment = ({
  open,
  setOpen,
  setTableData,
  isEdit,
  selectedRow = {},
  setSelectedRow,
  workflowList,
}) => {
  const [form] = Form.useForm();
  const handleException = useExceptionHandler();
  const appDeploymentsApiService = appDeploymentsService();
  const { setAlertDetails } = useAlertStore();
  const [backendErrors, setBackendErrors] = useState(null);

  const { Option } = Select;
  const [formDetails, setFormDetails] = useState(
    isEdit ? { ...selectedRow } : { ...defaultFromDetails }
  );

  const [isLoading, setIsLoading] = useState(false);

  const clearFormDetails = () => {
    setFormDetails({ ...defaultFromDetails });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleInputChange = (changedValues, allValues) => {
    setFormDetails({ ...formDetails, ...allValues });
    const changedFieldName = Object.keys(changedValues)[0];
    form.setFields([
      {
        name: changedFieldName,
        errors: [],
      },
    ]);
    setBackendErrors((prevErrors) => {
      if (prevErrors) {
        const updatedErrors = prevErrors.errors.filter(
          (error) => error.attr !== changedFieldName
        );
        return { ...prevErrors, errors: updatedErrors };
      }
      return null;
    });
  };

  const validateFormValues = () => {
    if (
      !formDetails?.app_display_name ||
      !formDetails?.app_name ||
      !formDetails?.workflow
    ) {
      setAlertDetails({
        type: "error",
        content: "Please enter all the fields.",
      });
      return false;
    }
    return true;
  };

  const updateTableData = () => {
    appDeploymentsApiService
      .getAppDeploymentsList()
      .then((res) => {
        setTableData(res?.data);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to update"));
      });
  };

  const createAppDeployment = () => {
    if (validateFormValues()) {
      setIsLoading(true);
      const body = formDetails;
      appDeploymentsApiService
        .createAppDeployment(body)
        .then((res) => {
          updateTableData();
          setSelectedRow(res?.data);
          setOpen(false);
          clearFormDetails();
          setAlertDetails({
            type: "success",
            content: "New App Deployment created successfully",
          });
        })
        .catch((err) => {
          setAlertDetails(handleException(err, "Failed to update"));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const updateAppDeployment = () => {
    if (validateFormValues()) {
      setIsLoading(true);
      const body = formDetails;

      appDeploymentsApiService
        .updateAppDeployment(body)
        .then((res) => {
          updateTableData();
          setOpen(false);
          clearFormDetails();
          setAlertDetails({
            type: "success",
            content: "App deployment updated successfully",
          });
        })
        .catch((err) => {
          setAlertDetails(handleException(err, "Failed to update"));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Modal
      title={isEdit ? "Update App deployment " : "Add App deployment"}
      centered
      maskClosable={false}
      open={open}
      onOk={isEdit ? updateAppDeployment : createAppDeployment}
      onCancel={handleCancel}
      okText={isEdit ? "Update" : "Save"}
      okButtonProps={{
        loading: isLoading,
      }}
      width={400}
    >
      <Form
        form={form}
        name="myForm"
        layout="vertical"
        initialValues={formDetails}
        onValuesChange={handleInputChange}
      >
        <Form.Item
          label="Display Name"
          name="app_display_name"
          rules={[{ required: true, message: "Please enter display name" }]}
          validateStatus={
            getBackendErrorDetail("pipeline_name", backendErrors) ? "error" : ""
          }
          help={getBackendErrorDetail("pipeline_name", backendErrors)}
        >
          <Input placeholder="Name" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          validateStatus={
            getBackendErrorDetail("description", backendErrors) ? "error" : ""
          }
          help={getBackendErrorDetail("description", backendErrors)}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="APP Name"
          name="app_name"
          rules={[
            { required: true, message: "Please enter APP Name" },
            {
              pattern: /^[a-zA-Z0-9_-]+$/,
              message:
                "Only letters, numbers, hyphen and underscores are allowed",
            },
            {
              pattern: /^.{1,30}$/,
              message: "Maximum 30 characters only allowed",
            },
          ]}
          validateStatus={
            getBackendErrorDetail("api_name", backendErrors) ? "error" : ""
          }
          help={getBackendErrorDetail("api_name", backendErrors)}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Workflow"
          name="workflow"
          rules={[{ required: true, message: "Please select an workflow" }]}
          validateStatus={
            getBackendErrorDetail("workflow", backendErrors) ? "error" : ""
          }
          help={getBackendErrorDetail("workflow", backendErrors)}
        >
          <Select>
            {workflowList.map((workflow) => {
              return (
                <Option value={workflow.workflow} key={workflow.workflow_name}>
                  {workflow.workflow_name}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};
AppDeployment.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  setTableData: PropTypes.func,
  isEdit: PropTypes.bool,
  selectedRow: PropTypes.object,
  setSelectedRow: PropTypes.func,
  workflowList: PropTypes.array,
};
export { AppDeployment };
